/**
 * nodeErrorCode — structured, locale-neutral error codes for node `data.error`.
 *
 * WHY THIS EXISTS (systemic rec #2 — "ban persisting localized English in node
 * data"): a node's `data.error` is persisted into YDoc, so it is shared across
 * collaborators and survives reload. If we wrote a localized (or English) prose
 * string there, a differently-localed collaborator — or the same user after
 * switching UI language — would see a message frozen in the wrong language.
 *
 * Instead, client-originated node errors persist a *code* (this envelope) and
 * are localized at RENDER time via {@link resolveNodeErrorMessage}. The `error`
 * field stays a plain `string` (the generated node-data schema owns its type),
 * so we serialize the code into a namespaced JSON envelope:
 *
 *   dw-err:{"code":"upload.tooLarge","params":{"type":"image","maxMb":10}}
 *
 * Backend/orchestrator-written prose (tracked separately in #2430) is NOT a
 * `dw-err:` envelope and therefore passes through {@link resolveNodeErrorMessage}
 * verbatim — this change is additive and never swallows existing messages.
 */

/**
 * The subset of a react-i18next `t` function this module needs: a key plus an
 * optional interpolation-params object, returning a string. A real
 * `useTranslation().t` (a react-i18next `TFunction`) is assignable to this, so
 * callers pass `t` directly and tests can supply a plain stub without casts.
 */
export type NodeErrorTranslate = (
  key: string,
  params?: Record<string, string | number>,
) => string

/** Prefix that marks a `data.error` string as a structured node-error envelope. */
export const NODE_ERROR_PREFIX = 'dw-err:'

/** The `upload.tooLarge` variant, named so size-check helpers can return it narrowly. */
export type NodeErrorTooLarge = {
  code: 'upload.tooLarge'
  params: { type: string; maxMb: number }
}

/**
 * Discriminated union of client-originated node error codes. Add new client
 * error sites here; each `code` maps to a `canvas:nodeError.<code>` i18n key.
 */
export type NodeErrorCode =
  | { code: 'upload.invalidType'; params: { accept: string } }
  | NodeErrorTooLarge
  | { code: 'upload.failed' }
  | { code: 'node.missingProject' }

/** Params object for any code that carries interpolation params. */
type NodeErrorParams = Record<string, string | number>

/** The persisted (possibly partial) shape after JSON.parse, before validation. */
type ParsedNodeError = { code?: string; params?: NodeErrorParams }

/**
 * Per-code param validators. A recognized code is only trusted when its
 * required params are present with the expected *primitive* types — otherwise
 * malformed interpolation data (e.g. `upload.tooLarge` with `maxMb: {}`) could
 * reach `t()`. `params` is JSON-parsed and only structurally typed as
 * `NodeErrorParams`, so validators re-check each value's runtime type. Codes
 * with no required params accept `undefined` or an empty object. Keep in sync
 * with {@link NodeErrorCode}.
 */
const isString = (v: string | number | undefined): boolean => typeof v === 'string'
const isNumber = (v: string | number | undefined): boolean =>
  typeof v === 'number' && Number.isFinite(v)

const CODE_VALIDATORS: Record<string, (params?: NodeErrorParams) => boolean> = {
  'upload.invalidType': (p) => isString(p?.accept),
  'upload.tooLarge': (p) => isString(p?.type) && isNumber(p?.maxMb),
  'upload.failed': () => true,
  'node.missingProject': () => true,
}

/** The set of recognized codes. Derived from the validator map. */
const KNOWN_CODES: ReadonlySet<string> = new Set(Object.keys(CODE_VALIDATORS))

/** Serialize a structured code into the persisted `data.error` string form. */
export function encodeNodeError(err: NodeErrorCode): string {
  return `${NODE_ERROR_PREFIX}${JSON.stringify(err)}`
}

/**
 * Parse a persisted `data.error` string back into a *recognized* structured
 * code. Returns `null` when the string is not a `dw-err:` envelope, is
 * malformed, carries an unknown code, or fails per-code param validation (e.g.
 * a required param is absent or a non-primitive) — so callers never surface raw
 * envelope internals, a missing i18n key, or invalid interpolation data.
 */
export function decodeNodeError(
  error: string | undefined | null,
): { code: string; params?: NodeErrorParams } | null {
  if (!error || !error.startsWith(NODE_ERROR_PREFIX)) return null
  try {
    const parsed: ParsedNodeError = JSON.parse(error.slice(NODE_ERROR_PREFIX.length))
    if (
      parsed &&
      typeof parsed.code === 'string' &&
      KNOWN_CODES.has(parsed.code) &&
      CODE_VALIDATORS[parsed.code](parsed.params)
    ) {
      return { code: parsed.code, params: parsed.params }
    }
  } catch {
    // Malformed envelope — fall through to null.
  }
  return null
}

/**
 * Resolve a persisted `data.error` string to a display message at render time.
 *
 * - A recognized `dw-err:` envelope is localized via `t('nodeError.<code>', params)`.
 * - A `dw-err:` envelope that is malformed OR carries an unknown code resolves
 *   to a generic localized message — the raw envelope is NEVER shown to users.
 * - Any other string (backend prose, legacy value) is returned unchanged so the
 *   backend-authoritative localization path (#2430) stays intact.
 * - Empty/undefined returns `undefined` so callers can omit the tooltip.
 *
 * @param t A `useTranslation('canvas')` t-function.
 */
export function resolveNodeErrorMessage(
  error: string | undefined | null,
  t: NodeErrorTranslate,
): string | undefined {
  if (!error) return undefined
  const decoded = decodeNodeError(error)
  if (decoded) return t(`nodeError.${decoded.code}`, { ...decoded.params })
  // A dw-err: envelope we could not decode (malformed / unknown code) must not
  // leak raw internals — show a generic localized message instead.
  if (error.startsWith(NODE_ERROR_PREFIX)) return t('nodeError.generic')
  // Not our envelope — backend/legacy prose passes through verbatim.
  return error
}
